import { useEffect, useState } from 'react';
import type { default as LottieComponent } from 'lottie-react';
import { useReducedMotion } from 'motion/react';
import { cn } from '@worldcoo/ui/lib/utils';

type LottieData = Record<string, unknown>;

/**
 * Client-only Lottie player. The library (`lottie-react` → `lottie-web`) touches
 * `window`/`document`, so it is loaded lazily after mount to stay SSR-safe. When the
 * user prefers reduced motion the animation is rendered paused on its first frame.
 */
export function LottiePlayer({
	animationData,
	className,
	loop = true,
	ariaLabel,
}: {
	animationData: LottieData;
	className?: string;
	loop?: boolean;
	ariaLabel?: string;
}) {
	const reduce = useReducedMotion() ?? false;
	const [Lottie, setLottie] = useState<typeof LottieComponent | null>(null);

	useEffect(() => {
		let alive = true;
		import('lottie-react').then((m) => {
			// `lottie-react` is CJS: Vite's interop can nest the component one level
			// deeper (`m.default.default`), so unwrap whichever shape we get.
			const d = m.default as typeof LottieComponent & { default?: typeof LottieComponent };
			if (alive) setLottie(() => d.default ?? d);
		});
		return () => {
			alive = false;
		};
	}, []);

	// Reserve the box so layout doesn't jump before the player hydrates.
	if (!Lottie) return <div className={className} aria-label={ariaLabel} role="img" />;

	return (
		<Lottie
			animationData={animationData}
			loop={reduce ? false : loop}
			autoplay={!reduce}
			className={cn(className)}
			aria-label={ariaLabel}
			role="img"
			rendererSettings={{ preserveAspectRatio: 'xMidYMid meet' }}
		/>
	);
}
